// src/features/cart/CartPage.jsx

import React from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../hooks/useCart";
import { formatCurrency } from "../../utils/formatCurrency";

const CartPage = () => {
  const { cart, removeFromCart, updateCartItem, clearCart } = useCart();
  const navigate = useNavigate();

  const totalPrice = cart.reduce(
    (total, item) => total + item.price * item.quantity,
    0
  );

  if (cart.length === 0) {
    return (
      <div className="text-center mt-10 px-2">
        <p>Your cart is empty.</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
        >
          ← Back
        </button>
      </div>
    );
  }

  const handleIncrease = (productId, quantity) => {
    updateCartItem(productId, quantity + 1);
  };

  const handleDecrease = (productId, quantity) => {
    if (quantity > 1) updateCartItem(productId, quantity - 1);
  };

  return (
    <div className="max-w-4xl mx-auto py-6 px-2">
      <button
        onClick={() => navigate(-1)}
        className="mb-4 px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
      >
        ← Back
      </button>

      <h1 className="text-3xl font-bold mb-6 text-center">Your Cart</h1>

      {/* Desktop Table */}
      <div className="hidden md:block">
        <table className="w-full table-auto border-collapse border border-gray-300">
          <thead>
            <tr className="bg-gray-200">
              <th className="border px-4 py-2">Item</th>
              <th className="border px-4 py-2">Price</th>
              <th className="border px-4 py-2">Quantity</th>
              <th className="border px-4 py-2">Subtotal</th>
              <th className="border px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {cart.map((item) => (
              <tr key={item.productId} className="text-center">
                <td className="border px-4 py-2">{item.title}</td>
                <td className="border px-4 py-2">{formatCurrency(item.price)}</td>
                <td className="border px-4 py-2 flex justify-center items-center space-x-2">
                  <button
                    onClick={() => handleDecrease(item.productId, item.quantity)}
                    className="px-2 bg-gray-300 rounded hover:bg-gray-400"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={(e) =>
                      updateCartItem(
                        item.productId,
                        parseInt(e.target.value, 10) || 1
                      )
                    }
                    className="w-16 border rounded px-2 py-1 text-center"
                  />
                  <button
                    onClick={() => handleIncrease(item.productId, item.quantity)}
                    className="px-2 bg-gray-300 rounded hover:bg-gray-400"
                  >
                    +
                  </button>
                </td>
                <td className="border px-4 py-2">
                  {formatCurrency(item.price * item.quantity)}
                </td>
                <td className="border px-4 py-2">
                  <button
                    onClick={() => removeFromCart(item.productId)}
                    className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-4">
        {cart.map((item) => (
          <div
            key={item.productId}
            className="border rounded-lg p-4 shadow flex flex-col space-y-2"
          >
            <div className="flex justify-between items-center">
              <h3 className="font-semibold">{item.title}</h3>
              <button
                onClick={() => removeFromCart(item.productId)}
                className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Remove
              </button>
            </div>

            <p>Price: {formatCurrency(item.price)}</p>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleDecrease(item.productId, item.quantity)}
                className="px-2 bg-gray-300 rounded hover:bg-gray-400"
              >
                -
              </button>
              <input
                type="number"
                min="1"
                value={item.quantity}
                onChange={(e) =>
                  updateCartItem(
                    item.productId,
                    parseInt(e.target.value, 10) || 1
                  )
                }
                className="w-16 border rounded px-2 py-1 text-center"
              />
              <button
                onClick={() => handleIncrease(item.productId, item.quantity)}
                className="px-2 bg-gray-300 rounded hover:bg-gray-400"
              >
                +
              </button>
            </div>

            <p className="font-bold">
              Subtotal: {formatCurrency(item.price * item.quantity)}
            </p>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="flex flex-col md:flex-row justify-between items-center mt-6 space-y-4 md:space-y-0">
        <div className="flex space-x-4">
          <button
            onClick={clearCart}
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            Clear Cart
          </button>
          <button
            onClick={() => navigate("/checkout")}
            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
          >
            Checkout
          </button>
        </div>
        <h2 className="text-2xl font-bold">Total: {formatCurrency(totalPrice)}</h2>
      </div>
    </div>
  );
};

export default CartPage;